import React, { useState, useEffect } from 'react';
import { Award, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import apiClient from '../../../../api/apiClient';

export default function RoleModal({
  open,
  data,
  departments,
  subDivisions,
  positions,
  saving,
  onClose,
  onSave,
}) {
  const [roleModal, setRoleModal] = useState({ open: false, data: null });

  // Function to generate code from name (client-side helper)
  const generateCodeFromName = name => {
    if (!name || name.trim() === '') {
      return '';
    }

    // Remove extra spaces and split by space
    const words = name.trim().split(/\s+/);

    // If only one word, take first 3 uppercase letters
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase().padEnd(3, 'X');
    }

    // If multiple words, take first letter of each word (up to 3 words)
    const initials = words
      .slice(0, 3)
      .map(word => word.charAt(0).toUpperCase())
      .join('');

    // Ensure at least 2 characters
    return initials.length >= 2 ? initials : initials.padEnd(2, 'X').toUpperCase();
  };

  useEffect(() => {
    if (open) {
      if (data && data.id) {
        // Edit mode - use existing data
        // Parse technical_skills and soft_skills if they're strings
        let parsedTechnicalSkills = data.technical_skills || [];
        let parsedSoftSkills = data.soft_skills || [];

        try {
          if (typeof data.technical_skills === 'string' && data.technical_skills) {
            parsedTechnicalSkills = JSON.parse(data.technical_skills);
          }
        } catch (e) {
          // Failed to parse technical_skills
          parsedTechnicalSkills = [];
        }

        try {
          if (typeof data.soft_skills === 'string' && data.soft_skills) {
            parsedSoftSkills = JSON.parse(data.soft_skills);
          }
        } catch (e) {
          // Failed to parse soft_skills
          parsedSoftSkills = [];
        }

        // Ensure arrays are properly formatted with name and proficiency
        parsedTechnicalSkills = Array.isArray(parsedTechnicalSkills)
          ? parsedTechnicalSkills.map(skill =>
              typeof skill === 'string'
                ? { name: skill, proficiency: 'intermediate' }
                : { name: skill.name || '', proficiency: skill.proficiency || 'intermediate' }
            )
          : [];

        parsedSoftSkills = Array.isArray(parsedSoftSkills)
          ? parsedSoftSkills.map(skill =>
              typeof skill === 'string'
                ? { name: skill, proficiency: 'intermediate' }
                : { name: skill.name || '', proficiency: skill.proficiency || 'intermediate' }
            )
          : [];

        const editData = {
          ...data,
          technical_skills: parsedTechnicalSkills,
          soft_skills: parsedSoftSkills,
          // Convert IDs to string for Select component
          division_id: data.division_id ? String(data.division_id) : '',
          sub_division_id: data.sub_division_id ? String(data.sub_division_id) : '',
          position_id: data.position_id ? String(data.position_id) : '',
        };
        setRoleModal({ open: true, data: editData });
      } else {
        // New role - initialize empty form
        const newForm = {
          code: '',
          name: '',
          description: '',
          status: 'active',
          technical_skills: [],
          soft_skills: [],
          division_id: '',
          sub_division_id: '',
          position_id: '',
        };
        setRoleModal({ open: true, data: newForm });
      }
    } else {
      setRoleModal({ open: false, data: null });
    }
  }, [open, data]);

  // Generate code when name changes (only for new roles)
  useEffect(() => {
    if (roleModal.data && !roleModal.data.id && roleModal.data.name) {
      const generateCode = async () => {
        try {
          const response = await apiClient.get('/hris/organization/roles/generate-code', {
            params: { name: roleModal.data.name },
          });
          if (response && response.code) {
            setRoleModal(prev => ({
              ...prev,
              data: { ...prev.data, code: response.code },
            }));
          }
        } catch (error) {
          // Error generating role code from backend
          // Fallback to client-side generation
          const generatedCode = generateCodeFromName(roleModal.data.name);
          if (generatedCode) {
            setRoleModal(prev => ({
              ...prev,
              data: { ...prev.data, code: generatedCode },
            }));
          }
        }
      };

      // Debounce the code generation
      const timeoutId = setTimeout(() => {
        generateCode();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [roleModal.data?.name, roleModal.data?.id, roleModal.data]);

  const handleSaveRole = async e => {
    e.preventDefault();

    // Validate required fields
    if (!roleModal.data?.name || roleModal.data.name.trim() === '') {
      alert('Nama Role wajib diisi');
      return;
    }

    // Ensure code is generated if not provided
    let codeToUse = roleModal.data?.code;
    if (!codeToUse || codeToUse.trim() === '') {
      if (roleModal.data?.name) {
        try {
          // Try to generate from backend first
          const response = await apiClient.get('/hris/organization/roles/generate-code', {
            params: { name: roleModal.data.name },
          });
          if (response && response.code) {
            codeToUse = response.code;
          } else {
            codeToUse = generateCodeFromName(roleModal.data.name);
          }
        } catch (error) {
          // Fallback to client-side generation
          codeToUse = generateCodeFromName(roleModal.data.name);
        }
      } else {
        alert('Kode role tidak dapat di-generate. Pastikan nama role sudah diisi.');
        return;
      }
    }

    // Prepare data to send
    const dataToSave = {
      ...roleModal.data,
      code: codeToUse,
      // Ensure technical_skills and soft_skills are arrays and filter empty ones
      technical_skills: Array.isArray(roleModal.data.technical_skills)
        ? roleModal.data.technical_skills
            .filter(skill => {
              // Remove empty skills
              if (typeof skill === 'string') {
                return skill.trim() !== '';
              }
              return skill?.name && skill.name.trim() !== '';
            })
            .map(skill => {
              // Ensure all skills have name and proficiency
              if (typeof skill === 'string') {
                return { name: skill, proficiency: 'intermediate' };
              }
              return {
                name: skill.name || '',
                proficiency: skill.proficiency || 'intermediate',
              };
            })
        : [],
      soft_skills: Array.isArray(roleModal.data.soft_skills)
        ? roleModal.data.soft_skills
            .filter(skill => {
              // Remove empty skills
              if (typeof skill === 'string') {
                return skill.trim() !== '';
              }
              return skill?.name && skill.name.trim() !== '';
            })
            .map(skill => {
              // Ensure all skills have name and proficiency
              if (typeof skill === 'string') {
                return { name: skill, proficiency: 'intermediate' };
              }
              return {
                name: skill.name || '',
                proficiency: skill.proficiency || 'intermediate',
              };
            })
        : [],
      // Convert empty strings to null for IDs, and ensure they're integers
      division_id:
        roleModal.data.division_id && roleModal.data.division_id !== ''
          ? typeof roleModal.data.division_id === 'string'
            ? parseInt(roleModal.data.division_id, 10)
            : roleModal.data.division_id
          : null,
      sub_division_id:
        roleModal.data.sub_division_id && roleModal.data.sub_division_id !== ''
          ? typeof roleModal.data.sub_division_id === 'string'
            ? parseInt(roleModal.data.sub_division_id, 10)
            : roleModal.data.sub_division_id
          : null,
      position_id:
        roleModal.data.position_id && roleModal.data.position_id !== ''
          ? typeof roleModal.data.position_id === 'string'
            ? parseInt(roleModal.data.position_id, 10)
            : roleModal.data.position_id
          : null,
      // Ensure status has a value
      status: roleModal.data.status || 'active',
    };

    onSave(dataToSave);
  };

  return (
    <Dialog open={roleModal.open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="!max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{roleModal.data?.id ? 'Edit' : 'Tambah'} Role & Kompetensi</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSaveRole} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold">Informasi Role</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kode *</Label>
                <Input
                  value={roleModal.data?.code || ''}
                  onChange={e =>
                    setRoleModal(prev => ({
                      ...prev,
                      data: { ...prev.data, code: e.target.value },
                    }))
                  }
                  required
                  readOnly={!roleModal.data?.id}
                  className={!roleModal.data?.id ? 'bg-gray-50' : ''}
                  title={
                    !roleModal.data?.id
                      ? 'Kode role di-generate otomatis'
                      : 'Kode role dapat diubah saat edit'
                  }
                />
                {!roleModal.data?.id && (
                  <p className="text-xs text-gray-500">Kode role di-generate otomatis</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Nama Role *</Label>
                <Input
                  value={roleModal.data?.name || ''}
                  onChange={e => {
                    const newName = e.target.value;
                    setRoleModal(prev => ({
                      ...prev,
                      data: { ...prev.data, name: newName },
                    }));
                    // Code will be generated in useEffect when name changes
                  }}
                  required
                />
                {!roleModal.data?.id && roleModal.data?.name && (
                  <p className="text-xs text-gray-500">
                    Kode akan di-generate otomatis dari nama role
                  </p>
                )}
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Status</Label>
                <Select
                  value={roleModal.data?.status || 'active'}
                  onValueChange={v =>
                    setRoleModal(prev => ({
                      ...prev,
                      data: { ...prev.data, status: v },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="inactive">Tidak Aktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Deskripsi</Label>
              <Textarea
                value={roleModal.data?.description || ''}
                onChange={e =>
                  setRoleModal(prev => ({
                    ...prev,
                    data: { ...prev.data, description: e.target.value },
                  }))
                }
                rows={3}
              />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              Kompetensi yang Dibutuhkan
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Technical Skills */}
              <div className="space-y-2">
                <Label className="text-blue-600">Technical Skills</Label>
                <div className="border rounded-lg p-3 bg-blue-50/30">
                  <div className="space-y-2">
                    {(roleModal.data?.technical_skills || []).map((skill, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={typeof skill === 'string' ? skill : skill.name || ''}
                          onChange={e => {
                            const newSkills = [...(roleModal.data?.technical_skills || [])];
                            newSkills[index] =
                              typeof skill === 'string'
                                ? { name: e.target.value, proficiency: 'intermediate' }
                                : { ...skill, name: e.target.value };
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, technical_skills: newSkills },
                            }));
                          }}
                          placeholder="Nama skill"
                          className="flex-1"
                        />
                        <Select
                          value={
                            typeof skill === 'string'
                              ? 'intermediate'
                              : skill.proficiency || 'intermediate'
                          }
                          onValueChange={v => {
                            const newSkills = [...(roleModal.data?.technical_skills || [])];
                            newSkills[index] =
                              typeof skill === 'string'
                                ? { name: skill, proficiency: v }
                                : { ...skill, proficiency: v };
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, technical_skills: newSkills },
                            }));
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                            <SelectItem value="expert">Expert</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newSkills = (roleModal.data?.technical_skills || []).filter(
                              (_, i) => i !== index
                            );
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, technical_skills: newSkills },
                            }));
                          }}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newSkills = [
                          ...(roleModal.data?.technical_skills || []),
                          { name: '', proficiency: 'intermediate' },
                        ];
                        setRoleModal(prev => ({
                          ...prev,
                          data: { ...prev.data, technical_skills: newSkills },
                        }));
                      }}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Tambah Technical Skill
                    </Button>
                  </div>
                </div>
              </div>

              {/* Soft Skills */}
              <div className="space-y-2">
                <Label className="text-purple-600">Soft Skills</Label>
                <div className="border rounded-lg p-3 bg-purple-50/30">
                  <div className="space-y-2">
                    {(roleModal.data?.soft_skills || []).map((skill, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={typeof skill === 'string' ? skill : skill.name || ''}
                          onChange={e => {
                            const newSkills = [...(roleModal.data?.soft_skills || [])];
                            newSkills[index] =
                              typeof skill === 'string'
                                ? { name: e.target.value, proficiency: 'intermediate' }
                                : { ...skill, name: e.target.value };
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, soft_skills: newSkills },
                            }));
                          }}
                          placeholder="Nama skill"
                          className="flex-1"
                        />
                        <Select
                          value={
                            typeof skill === 'string'
                              ? 'intermediate'
                              : skill.proficiency || 'intermediate'
                          }
                          onValueChange={v => {
                            const newSkills = [...(roleModal.data?.soft_skills || [])];
                            newSkills[index] =
                              typeof skill === 'string'
                                ? { name: skill, proficiency: v }
                                : { ...skill, proficiency: v };
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, soft_skills: newSkills },
                            }));
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                            <SelectItem value="expert">Expert</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newSkills = (roleModal.data?.soft_skills || []).filter(
                              (_, i) => i !== index
                            );
                            setRoleModal(prev => ({
                              ...prev,
                              data: { ...prev.data, soft_skills: newSkills },
                            }));
                          }}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newSkills = [
                          ...(roleModal.data?.soft_skills || []),
                          { name: '', proficiency: 'intermediate' },
                        ];
                        setRoleModal(prev => ({
                          ...prev,
                          data: { ...prev.data, soft_skills: newSkills },
                        }));
                      }}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Tambah Soft Skill
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Mapping Organisasi</h3>

            <div className="space-y-2">
              <Label>Divisi</Label>
              <Select
                value={roleModal.data?.division_id || ''}
                onValueChange={value => {
                  setRoleModal(prev => ({
                    ...prev,
                    data: {
                      ...prev.data,
                      division_id: value,
                      sub_division_id: '', // Reset sub divisi when divisi changes
                    },
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih divisi" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Sub Divisi</Label>
              <Select
                value={roleModal.data?.sub_division_id || ''}
                onValueChange={value => {
                  setRoleModal(prev => ({
                    ...prev,
                    data: { ...prev.data, sub_division_id: value },
                  }));
                }}
                disabled={!roleModal.data?.division_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih sub divisi" />
                </SelectTrigger>
                <SelectContent>
                  {subDivisions
                    .filter(s => s.department_id === roleModal.data?.division_id)
                    .map(sub => (
                      <SelectItem key={sub.id} value={String(sub.id)}>
                        {sub.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Jabatan</Label>
              <Select
                value={roleModal.data?.position_id || ''}
                onValueChange={value => {
                  setRoleModal(prev => ({
                    ...prev,
                    data: { ...prev.data, position_id: value },
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih jabatan" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map(pos => (
                    <SelectItem key={pos.id} value={String(pos.id)}>
                      {pos.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Batal
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
